/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastModifyBy: pein
 * @LastModifyTime: 2024-12-17 14:58:23
 */

import React, { useState } from 'react'
import { Button, Popconfirm, Table } from 'antd'
import { DepartmentMember } from '../../global'
import CONFIG from '../../constants/config'
import dayjs from 'dayjs'
import sensors from '../../utils/sensors'
import { capturePromiseException } from '../../utils/sentryUtils'
import { useAppDispatch, useAppSelector, useIsAdmin } from '../../app/hooks'
import { RenameModal } from './components'
import { renameUser } from '../../actions/team'
import {
  cancelUserAsDepartmentAdmin,
  setUserAsDepartmentAdmin
} from '../../actions/group'

const Column = Table.Column

interface MemberTableProps {
  dataSource: DepartmentMember[]
  rowSelection: any
  refreshData: () => void
  handleDeleteClick: (members: DepartmentMember[]) => void
}

const MemberTable = (props: MemberTableProps) => {
  const { dataSource, rowSelection, refreshData, handleDeleteClick } = props
  const dispatch = useAppDispatch()
  const groupId = useAppSelector((store) => store.group.groupId)
  const [renameModalVisible, setRenameModalVisible] = useState(false)
  const [toRenameUser, setToRenameUser] = useState<DepartmentMember | null>(
    null
  )
  const curDepartment = useAppSelector(
    (store) => store.organization.curDepartment
  )
  const isAdmin = useIsAdmin()

  const handleRenameUser = (newName: string) => {
    dispatch(
      renameUser({
        group_id: groupId,
        member_id: toRenameUser?.userID,
        nickname: newName
      })
    )
      .then(() => {
        refreshData()
        setRenameModalVisible(false)
      })
      .catch(capturePromiseException)
  }

  const handleSetUserAsDepartmentAdmin = (user: DepartmentMember) => {
    dispatch(
      setUserAsDepartmentAdmin({
        groupID: groupId,
        userIDs: [user.userID]
      })
    )
      .then(() => {
        refreshData()
      })
      .catch(capturePromiseException)
  }

  const handleCancelUserAsDepartmentAdmin = (user: DepartmentMember) => {
    dispatch(
      cancelUserAsDepartmentAdmin({
        groupID: groupId,
        userIDs: [user.userID]
      })
    )
      .then(() => {
        refreshData()
      })
      .catch(capturePromiseException)
  }

  return (
    <>
      <Table
        pagination={false}
        scroll={{ y: window.innerHeight - 340 }}
        dataSource={dataSource}
        rowKey="departmentUserID"
        rowSelection={rowSelection}
      >
        <Column
          title="姓名"
          width={220}
          dataIndex="nickname"
          render={(text, record: DepartmentMember) => {
            return (
              <span>
                <img
                  style={{ width: '21px', marginRight: '8px' }}
                  src={record.headImgUrl}
                  alt=""
                />
                <span>{text}</span>
              </span>
            )
          }}
        />
        <Column title="手机号" dataIndex="mobile" />
        <Column
          title="部门"
          ellipsis
          render={(record) => record.departmentNames.join('、')}
        />
        <Column
          title="角色"
          dataIndex="roleType"
          render={(text) => {
            const role = CONFIG.memberRoles.find((role) => role.id === text)
            return role ? role.role : ''
          }}
        />
        <Column
          title="上次登录时间"
          dataIndex="activeTime"
          render={(text) => {
            const lastActive = dayjs.unix(Number(text))
            const diffDays = dayjs().diff(lastActive, 'days')
            if (diffDays > 30) {
              return '30天前'
            } else if (diffDays === 0) {
              return '今天'
            } else {
              return `${diffDays}天前`
            }
          }}
        />
        <Column
          title="操作"
          width={320}
          align="right"
          render={(record) => {
            const actions = [
              <Button
                key="1"
                type="link"
                onClick={() => {
                  sensors.track('web_click_page_workgroup_groupcontacts', {
                    clickItem: 'modifyName',
                    groupID: groupId
                  })
                  setToRenameUser(record)
                  setRenameModalVisible(true)
                }}
              >
                修改姓名
              </Button>,
              <Button
                key="2"
                type="link"
                style={{ color: '#D40000' }}
                onClick={() => {
                  sensors.track('web_click_page_workgroup_groupcontacts', {
                    clickItem: 'deleteMember',
                    groupID: groupId
                  })
                  handleDeleteClick([record])
                }}
              >
                删除
              </Button>
            ]
            // 如果登录用户是管理员或主管理员，则显示设为部门管理员按钮
            // 如果当前不是部门，而是根级团队，不显示设为部门管理员按钮
            // 如果当前用户普通成员，才显示设为部门管理员按钮
            if (isAdmin && curDepartment?.id !== '0' && record.roleType === 0) {
              actions.unshift(
                <Button
                  key="0"
                  type="link"
                  onClick={() => {
                    sensors.track('web_click_page_workgroup_groupcontacts', {
                      clickItem: 'setDepartmentAdmin',
                      groupID: groupId
                    })
                    handleSetUserAsDepartmentAdmin(record)
                  }}
                >
                  设为部门管理员
                </Button>
              )
            }
            // 如果已是部门管理员，则显示取消部门管理员按钮
            if (isAdmin && curDepartment?.id !== '0' && record.roleType === 3) {
              actions.unshift(
                <Popconfirm
                  key="0"
                  title="确定取消部门管理员吗？"
                  onConfirm={() => {
                    sensors.track('web_click_page_workgroup_groupcontacts', {
                      clickItem: 'cancelDepartmentAdmin',
                      groupID: groupId
                    })
                    handleCancelUserAsDepartmentAdmin(record)
                  }}
                >
                  <Button type="link" style={{ color: '#D40000' }}>
                    取消部门管理员
                  </Button>
                </Popconfirm>
              )
            }
            return actions
          }}
        />
      </Table>
      <RenameModal
        visible={renameModalVisible}
        defaultName={toRenameUser?.nickname || ''}
        onCancel={() => setRenameModalVisible(false)}
        onOk={handleRenameUser}
      />
    </>
  )
}

export default MemberTable
